import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  operatorApi,
  type Tenant, type TenantCreate, type TenantUpdate,
} from '../../api/operator'
import { useAdminToken } from './AdminLayout'
import { Badge, type BadgeTone } from '../../components/Badge'

const MODULE_OPTIONS = [
  { value: 'meter_reading', label: 'Očitavanje vodomera', short: 'MR' },
  { value: 'repair',        label: 'Popravke i servisi',  short: 'REP' },
  { value: 'scada',         label: 'SCADA nadzor',        short: 'SCADA' },
]

const MODULE_TONE: Record<string, BadgeTone> = {
  meter_reading: 'teal',
  repair:        'warning',
  scada:         'info',
}

const MODULE_SHORT: Record<string, string> = {
  meter_reading: 'MR',
  repair:        'REP',
  scada:         'SCADA',
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysLeft(iso: string | null): number | null {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

type StatusFilter = 'all' | 'active' | 'expired' | 'inactive'

function tenantStatusInfo(t: Tenant): { label: string; tone: BadgeTone; bucket: StatusFilter } {
  if (!t.active) return { label: 'Neaktivan', tone: 'critical', bucket: 'inactive' }
  const d = daysLeft(t.license_expires_at)
  if (d !== null && d < 0)  return { label: 'Istekla lic.', tone: 'critical', bucket: 'expired' }
  if (d !== null && d < 30) return { label: `${d}d`,        tone: 'warning',  bucket: 'active' }
  return { label: 'Aktivan', tone: 'success', bucket: 'active' }
}

// ── Shared input style ────────────────────────────────────────────────────────

const inputCls = 'w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden'

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

// ── Create tenant modal ───────────────────────────────────────────────────────

function CreateTenantModal({ onClose }: { onClose: () => void }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [form, setForm] = useState<TenantCreate>({ name: '', municipality: '', seats: 10, modules: [] })
  const [err, setErr] = useState('')

  const toggleModule = (m: string) =>
    setForm((f) => ({
      ...f,
      modules: f.modules?.includes(m) ? f.modules.filter((x) => x !== m) : [...(f.modules ?? []), m],
    }))

  const set = (key: keyof TenantCreate) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: key === 'seats' ? Number(e.target.value) : e.target.value }))

  const mutation = useMutation({
    mutationFn: () => {
      const body: TenantCreate = { ...form }
      if (!body.contact_email) delete body.contact_email
      if (!body.contact_phone) delete body.contact_phone
      if (!body.license_expires_at) delete body.license_expires_at
      return operatorApi.createTenant(token, body)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['op-tenants'] }); onClose() },
    onError: (e: unknown) => {
      qc.invalidateQueries({ queryKey: ['op-tenants'] })
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri kreiranju.')
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-5">Novi tenant</h2>

        <div className="space-y-4">
          {([
            { key: 'name'          as const, label: 'Naziv organizacije', required: true },
            { key: 'municipality'  as const, label: 'Opština / grad',     required: true },
            { key: 'contact_email' as const, label: 'Kontakt email' },
            { key: 'contact_phone' as const, label: 'Kontakt telefon' },
          ]).map(({ key, label, required }) => (
            <Field key={key} label={label} required={required}>
              <input type="text" value={(form[key] as string) ?? ''} onChange={set(key)} className={inputCls} />
            </Field>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Broj sedišta" required>
              <input type="number" min={1} max={500} value={form.seats} onChange={set('seats')} className={inputCls} />
            </Field>
            <Field label="Licenca ističe">
              <input type="date" value={form.license_expires_at ?? ''} onChange={set('license_expires_at')} className={inputCls} />
            </Field>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Moduli</label>
            <div className="flex flex-col gap-2">
              {MODULE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={form.modules?.includes(value) ?? false}
                    onChange={() => toggleModule(value)}
                    className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || !form.name || !form.municipality}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Kreiranje…' : 'Kreirati tenant'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Edit tenant modal ─────────────────────────────────────────────────────────

function EditTenantModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [form, setForm] = useState<TenantUpdate>({
    modules:            tenant.modules ?? [],
    seats:              tenant.seats,
    license_expires_at: tenant.license_expires_at ?? undefined,
    active:             tenant.active,
  })
  const [err, setErr] = useState('')

  const toggleModule = (m: string) =>
    setForm((f) => ({
      ...f,
      modules: f.modules?.includes(m) ? f.modules.filter((x) => x !== m) : [...(f.modules ?? []), m],
    }))

  const mutation = useMutation({
    mutationFn: () => operatorApi.updateTenant(token, tenant.id, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['op-tenants'] }); onClose() },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri izmeni.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Izmeni tenant</h2>
        <p className="text-xs text-gray-500 mb-5">{tenant.name} · {tenant.municipality}</p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Broj sedišta">
              <input type="number" min={1} max={500} value={form.seats ?? tenant.seats}
                onChange={(e) => setForm((f) => ({ ...f, seats: Number(e.target.value) }))}
                className={inputCls} />
            </Field>
            <Field label="Licenca ističe">
              <input type="date"
                value={form.license_expires_at ? form.license_expires_at.split('T')[0] : ''}
                onChange={(e) => setForm((f) => ({ ...f, license_expires_at: e.target.value || undefined }))}
                className={inputCls} />
            </Field>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Moduli</label>
            <div className="flex flex-col gap-2">
              {MODULE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={form.modules?.includes(value) ?? false}
                    onChange={() => toggleModule(value)}
                    className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={form.active ?? tenant.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
            <span className="text-sm text-gray-700">Tenant aktivan</span>
          </label>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Čuvanje…' : 'Sačuvati izmene'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Delete tenant modal ───────────────────────────────────────────────────────

function DeleteTenantModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const token  = useAdminToken()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState('')
  const [err, setErr]         = useState('')

  const mutation = useMutation({
    mutationFn: () => operatorApi.deleteTenant(token, tenant.id, confirm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['op-tenants'] })
      navigate('/admin/tenants')
      onClose()
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri brisanju.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white border border-red-200 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Obrisati tenant</h2>
          <p className="text-xs text-gray-500 mt-1">Ova akcija je nepovratna. Svi podaci tenanta biće trajno obrisani.</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800">
          Unesite tačan naziv: <span className="font-mono font-semibold">{tenant.name}</span>
        </div>

        <Field label="Potvrda naziva">
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder={tenant.name} className={inputCls} />
        </Field>

        {err && <p className="text-xs text-red-600">{err}</p>}

        <div className="flex gap-2">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || confirm !== tenant.name}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Brisanje…' : 'Obrisati'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminTenants() {
  const token  = useAdminToken()
  const navigate = useNavigate()

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['op-tenants', token],
    queryFn: () => operatorApi.listTenants(token).then((r) => r.data),
    staleTime: 60_000,
  })

  const [search,   setSearch]   = useState('')
  const [status,   setStatus]   = useState<StatusFilter>('all')
  const [modules,  setModules]  = useState<string[]>([])
  const [creating, setCreating] = useState(false)
  const [editing,  setEditing]  = useState<Tenant | null>(null)
  const [deleting, setDeleting] = useState<Tenant | null>(null)

  const toggleModule = (m: string) =>
    setModules((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return tenants.filter((t) => {
      if (q && !t.name.toLowerCase().includes(q) && !t.municipality.toLowerCase().includes(q)) return false
      if (status !== 'all' && tenantStatusInfo(t).bucket !== status) return false
      if (modules.length > 0 && !modules.some((m) => (t.modules ?? []).includes(m))) return false
      return true
    })
  }, [tenants, search, status, modules])

  const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: 'all',      label: 'Svi' },
    { value: 'active',   label: 'Aktivni' },
    { value: 'expired',  label: 'Istekli' },
    { value: 'inactive', label: 'Neaktivni' },
  ]

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Tenanti</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isLoading ? 'Učitavanje…' : `${tenants.length} organizacija`}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Novi tenant
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraži po nazivu ili opštini…"
            className="w-full bg-white border border-gray-300 text-sm rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5">
            {statusFilters.map(({ value, label }) => (
              <button key={value} onClick={() => setStatus(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  status === value
                    ? 'bg-brand-cyan text-brand-midnight'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {label}
              </button>
            ))}
          </div>

          <div className="h-4 w-px bg-gray-200" />

          <div className="flex gap-3">
            {MODULE_OPTIONS.map(({ value, short }) => (
              <label key={value} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input type="checkbox" checked={modules.includes(value)} onChange={() => toggleModule(value)}
                  className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
                <span className="text-xs text-gray-600">{short}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-gray-500">
                {tenants.length === 0 ? 'Nema tenanta. Kreirajte prvog.' : 'Nema rezultata za izabrane filtere.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Naziv', 'Opština', 'Moduli', 'Sedišta', 'Licenca', 'Status', 'Akcije'].map((h) => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const { label, tone } = tenantStatusInfo(t)
                    return (
                      <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-900">
                          <div className="flex items-center gap-1.5">
                            <span>{t.name}</span>
                            {t.is_template && (
                              <Badge tone="warning" title="Šablon za noćni reset Vodograda — nije vidljiv korisnicima">
                                ŠABLON
                              </Badge>
                            )}
                            {t.is_demo && (
                              <Badge tone="neutral" title="Demo tenant">
                                DEMO
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-gray-500">{t.municipality}</td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(t.modules ?? []).length === 0
                              ? <span className="text-xs text-gray-400">—</span>
                              : (t.modules ?? []).map((m) => (
                                  <Badge key={m} tone={MODULE_TONE[m] ?? 'neutral'}>
                                    {MODULE_SHORT[m] ?? m}
                                  </Badge>
                                ))
                            }
                          </div>
                        </td>
                        <td className="px-5 py-3 text-gray-500">{t.seats}</td>
                        <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{fmtDate(t.license_expires_at)}</td>
                        <td className="px-5 py-3">
                          <Badge tone={tone}>{label}</Badge>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => navigate(`/admin/tenants/${t.id}`)}
                              className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors">
                              Detalji
                            </button>
                            <button onClick={() => setEditing(t)}
                              className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors">
                              Izmeni
                            </button>
                            <button onClick={() => setDeleting(t)}
                              className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors">
                              Obriši
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {creating && <CreateTenantModal onClose={() => setCreating(false)} />}
      {editing   && <EditTenantModal   tenant={editing}  onClose={() => setEditing(null)} />}
      {deleting  && <DeleteTenantModal tenant={deleting} onClose={() => setDeleting(null)} />}
    </div>
  )
}
